import cx from 'classnames'
import { useCallback, useContext } from 'react'

import {
  SanityProductFragment,
  SanityProductOption,
  SanityProductOptionSetting,
} from '@data/sanity/queries/types/product'
import { hasObject } from '@lib/helpers'
import { ProductVariantPreviewContext } from '@lib/product-variant-preview-context'
import {
  PartialProductVariantOption,
  ProductVariantSelectionContext,
} from '@lib/product-variant-selection-context'
import { StringsContext } from '@lib/strings-context'

import RadioGroupItem from '@components/radio-group-item'
import Swatch from '@components/swatch'

interface ProductOptionValueProps {
  value: string
  product: SanityProductFragment
  option: SanityProductOption
  optionSettings: SanityProductOptionSetting[]
  strictMatch?: boolean
  swatchSize?: number
  className?: string
  labelClassName?: string
}

const ProductOptionValue = ({
  value,
  product,
  option,
  optionSettings,
  strictMatch,
  swatchSize,
  className,
  labelClassName,
}: ProductOptionValueProps) => {
  const strings = useContext(StringsContext)
  const { setPreviewVariantId } = useContext(ProductVariantPreviewContext)
  const { selectedOptions } = useContext(ProductVariantSelectionContext)

  const optionColor = optionSettings.find((optionSetting) => {
    const optionParts = optionSetting.forOption?.split(':')

    return optionParts?.[0] === option.name && optionParts?.[1] === value
  })?.color

  const handleMouseEnter = useCallback(() => {
    const previewVariantOptions: PartialProductVariantOption[] = [
      ...(selectedOptions?.filter(
        (selectedOption) => selectedOption.name !== option.name,
      ) ?? []),
      {
        name: option.name,
        value,
      },
    ]

    // Find variant that matches preview options
    const newVariant = product.variants?.find(({ options }) =>
      previewVariantOptions.every((previewVariantOption) =>
        hasObject(options, previewVariantOption),
      ),
    )

    if (newVariant) {
      setPreviewVariantId(newVariant.id)
    }
  }, [product, option.name, value, setPreviewVariantId, selectedOptions])

  const handleMouseLeave = useCallback(() => {
    setPreviewVariantId(null)
  }, [setPreviewVariantId])

  // With strict matching, match all options, otherwise match just this option
  const currentOption: PartialProductVariantOption = {
    name: option.name,
    value,
  }
  const potentialOptions: PartialProductVariantOption[] = [
    ...(selectedOptions?.filter(
      (selectedOption) => selectedOption.name !== option.name,
    ) ?? []),
    currentOption,
  ]
  const potentialVariant = product.variants?.find((variant) =>
    variant.options.every((variantOption) =>
      hasObject(potentialOptions, variantOption),
    ),
  )
  const isInStock = strictMatch
    ? !!potentialVariant?.inStock
    : !!product.variants?.some(
        (variant) =>
          !!variant.inStock &&
          variant.options.some((variantOption) =>
            hasObject([currentOption], variantOption),
          ),
      )

  const isActive = !!selectedOptions?.some(
    (selectedOption) =>
      selectedOption.name === option.name && selectedOption.value === value,
  )
  const isDisabled = !isInStock && !!potentialVariant && !isActive

  return (
    <RadioGroupItem
      value={value}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cx(
        {
          'p-0 bg-transparent': !!optionColor,
        },
        className,
      )}
    >
      {!!optionColor && (
        <Swatch
          color={optionColor}
          label={strings.productColorOptionLabel
            .replace(/{value}/gi, value)
            .replace(/{name}/gi, option.name.toLowerCase())}
          size={swatchSize}
          isActive={isActive}
          isCrossed={isDisabled}
        />
      )}

      {!optionColor && (
        <div
          className={cx(
            {
              'opacity-30': isDisabled,
            },
            labelClassName,
          )}
        >
          {value}
        </div>
      )}
    </RadioGroupItem>
  )
}

export default ProductOptionValue
